import {useEffect, useState, useRef, useCallback} from "react";

const ADMIN_CHAT_POSITION_KEY = 'sbAdminChatSidebarPosition';

export function useAdminSidebarDrag(isAdmin, sidebarMode) {
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const [positionStyle, setPositionStyle] = useState({});

    // Restore saved position when entering sidebar mode, clear when leaving
    useEffect(() => {
        if (!isAdmin || !sidebarMode) {
            setPositionStyle({});
            return;
        }
        try {
            const saved = localStorage.getItem(ADMIN_CHAT_POSITION_KEY);
            if (saved) {
                const pos = JSON.parse(saved);
                const clampedLeft = Math.max(0, Math.min(pos.left, window.innerWidth - pos.width));
                const clampedTop  = Math.max(0, Math.min(pos.top, window.innerHeight - 50));
                setPositionStyle({
                    top: clampedTop + 'px',
                    left: clampedLeft + 'px',
                    right: 'auto',
                    bottom: 'auto',
                    width: pos.width + 'px',
                    height: pos.height + 'px',
                });
            }
        } catch (e) { /* ignore */ }
    }, [isAdmin, sidebarMode]);

    // Clamp position (left/top) when the viewport is resized
    useEffect(() => {
        if (!isAdmin || !sidebarMode) return;

        const onResize = () => {
            setPositionStyle(prev => {
                if (!prev.left && !prev.top) return prev;
                const top   = parseFloat(prev.top)   || 0;
                const left  = parseFloat(prev.left)  || 0;
                const width = parseFloat(prev.width) || 0;
                const clampedLeft = Math.max(0, Math.min(left, window.innerWidth - width));
                const clampedTop  = Math.max(0, Math.min(top, window.innerHeight - 50));
                return { ...prev, left: clampedLeft + 'px', top: clampedTop + 'px' };
            });
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isAdmin, sidebarMode]);

    // Global mousemove / mouseup listeners for drag tracking
    useEffect(() => {
        if (!isAdmin || !sidebarMode) return;

        const onMouseMove = (e) => {
            if (!isDragging.current || !containerRef.current) return;
            const container = containerRef.current;
            const newLeft = Math.max(0, Math.min(
                e.clientX - dragOffset.current.x,
                window.innerWidth - container.offsetWidth
            ));
            const newTop = Math.max(0, Math.min(
                e.clientY - dragOffset.current.y,
                window.innerHeight - Math.min(container.offsetHeight, 50)
            ));
            setPositionStyle(prev => ({ ...prev, top: newTop + 'px', left: newLeft + 'px' }));
        };

        const onMouseUp = () => {
            if (!isDragging.current) return;
            isDragging.current = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            try {
                localStorage.setItem(ADMIN_CHAT_POSITION_KEY, JSON.stringify({
                    top: rect.top, left: rect.left,
                    width: rect.width, height: rect.height,
                }));
            } catch (e) { /* ignore */ }
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isAdmin, sidebarMode]);

    const onHeaderMouseDown = useCallback((e) => {
        if (!isAdmin || !sidebarMode || e.button !== 0 || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        setPositionStyle({
            top: rect.top + 'px',
            left: rect.left + 'px',
            right: 'auto',
            bottom: 'auto',
            width: rect.width + 'px',
            height: rect.height + 'px',
        });
        isDragging.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
        e.preventDefault();
    }, [isAdmin, sidebarMode]);

    return { containerRef, positionStyle, onHeaderMouseDown };
}
